import { asObject } from 'core/api/unwrap';
import { ApiError } from 'core/errors/ApiError';

function malformedOptions() {
  return new ApiError({
    status: 0,
    message: 'Request failed',
    isMalformed: true,
  });
}

function positiveInt(value, fallback) {
  const number = Number(value);
  if (Number.isFinite(number) && number > 0) {
    return Math.floor(number);
  }
  return fallback;
}

function asOptionList(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  const items = value.map((item) => {
    if (typeof item === 'string') {
      const code = item.trim();
      return code ? { code, label: code } : null;
    }
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return null;
    }
    const code = item.code ?? item.value ?? item.id;
    const label = item.label ?? item.name ?? item.title;
    if (code == null || String(code).trim() === '' || label == null || String(label).trim() === '') {
      return null;
    }
    return {
      code: String(code).trim(),
      label: String(label).trim(),
    };
  }).filter(Boolean);
  return items.length > 0 ? items : null;
}

function pickList(data, optionsBag, keys) {
  for (const key of keys) {
    const list = asOptionList(data[key]) || asOptionList(optionsBag[key]);
    if (list) {
      return list;
    }
  }
  return null;
}

function asStringList(value, fallback) {
  if (!Array.isArray(value)) {
    return fallback;
  }
  const items = value.map((item) => String(item || '').trim()).filter(Boolean);
  return items.length > 0 ? items : fallback;
}

export function adaptProfileOptions(raw) {
  const data = asObject(raw);
  const optionsBag = asObject(data.options);
  const ageRanges = pickList(data, optionsBag, ['age_ranges', 'age_range', 'ageRanges']);
  const educationLevels = pickList(data, optionsBag, ['education_levels', 'education_level', 'educationLevels']);
  const workExperience = pickList(data, optionsBag, ['work_experience', 'work_experiences', 'workExperience']);
  const countries = pickList(data, optionsBag, ['countries', 'country']);

  if (!ageRanges || !educationLevels || !workExperience || !countries) {
    throw malformedOptions();
  }

  const defaults = asObject(data.defaults);
  const limitsRaw = asObject(data.limits);
  const skillsLimits = asObject(limitsRaw.skills);
  const nameLimits = asObject(limitsRaw.full_name);
  const fieldLimits = asObject(limitsRaw.experience_field);
  const requirements = asObject(data.requirements);
  const conditional = asObject(data.conditional_requirements || data.conditionalRequirements);
  const employedWhenTrue = asStringList(
    asObject(conditional.is_employed).true,
    asStringList(requirements.required_when_employed, ['work_experience', 'experience_field']),
  );
  const requiredFields = asStringList(
    data.required_fields,
    asStringList(requirements.required, [
      'full_name',
      'age_range',
      'education_level',
      'is_employed',
      'skills',
      'country',
    ]),
  );

  const defaultCountry = normalizeListedCode(defaults.country, countries) || listedCode(countries, 'SA');
  const skillsRequired = requiredFields.includes('skills');

  return {
    contract_version: data.contract_version == null ? 2 : data.contract_version,
    extended_fields_required: data.extended_fields_required !== false,
    defaults: {
      country: defaultCountry,
    },
    age_ranges: ageRanges,
    education_levels: educationLevels,
    work_experience: workExperience,
    countries,
    required_fields: requiredFields,
    conditional_requirements: {
      is_employed: {
        true: employedWhenTrue,
      },
    },
    limits: {
      skills: {
        min_items: positiveInt(
          skillsLimits.min_items ?? limitsRaw.skills_min_items,
          skillsRequired ? 1 : 0,
        ),
        max_items: positiveInt(skillsLimits.max_items ?? limitsRaw.skills_max_items, 20),
        max_length: positiveInt(
          skillsLimits.max_length ?? limitsRaw.skill_max_length ?? limitsRaw.skills_max_length,
          80,
        ),
      },
      full_name: {
        min: positiveInt(nameLimits.min ?? limitsRaw.full_name_min, 2),
        max: positiveInt(nameLimits.max ?? limitsRaw.full_name_max, 150),
      },
      experience_field: {
        max: positiveInt(
          fieldLimits.max ?? limitsRaw.experience_field_max ?? limitsRaw.experience_field_max_length,
          150,
        ),
      },
    },
  };
}

function listedCode(options, code) {
  return options.some((item) => item.code === code) ? code : null;
}

export function normalizeListedCode(value, options) {
  if (value == null || !Array.isArray(options)) {
    return null;
  }
  const code = String(value).trim();
  return listedCode(options, code);
}

export function defaultCountryCode(options) {
  return options?.defaults?.country || listedCode(options?.countries || [], 'SA') || null;
}

export function labelForCode(options, code) {
  if (!code || !Array.isArray(options)) {
    return null;
  }
  return options.find((item) => item.code === code)?.label || null;
}

export function employmentFieldsRequired(isEmployed, options) {
  if (isEmployed !== true) {
    return false;
  }
  const fields = options?.conditional_requirements?.is_employed?.true;
  if (!Array.isArray(fields)) {
    return true;
  }
  return fields.length > 0;
}
